

import { Component, Vue, Watch, Prop } from "vue-property-decorator"
import { namespace, Getter } from "vuex-class"
import vSelect from "vue-select"
import Numeric from "../../Fields/Numeric.vue"
import UserRoleSetup from "./UserRoleSetup.vue"
import api from "@/services/api"
import { MarkerColors } from "@/services/mapProvider"
import { caseColumns } from "@/types/caseColumns"
import { TenantFeatures } from "@/types/tenantFeatures"
import DefinitionTitle from "../Definitions/DefinitionTitle.vue"
import FieldExpression from "@/components/Fields/FieldExpression.vue"
import AddToDate from "@/components/Fields/AddToDate.vue"
import { LabeledValue } from "@/types/labeledValue"
import { AddToDateInputArgs } from "@/types/fields/addToDate"
import { StoreModules } from "@/store/StoreModuleTypes"

import { Field, DataDefinitionWithFields, DataType, FieldInstance } from "@/types"
import {
	CalculatedValueAddToDate,
	CalculationDataTypeValue,
	CalculationType,
	FieldExtensionsIds,
	getExpressionFieldIds,
	formatFieldsForDefinitionTitle
} from "@/types/field"

const setup = namespace(StoreModules.Setup)

interface CalculationTypeOption {
	key: CalculationType;
	label: string;
}

interface NumericalCalculationParams {
	add?: string[];
	subtract?: string[];
	static?: number;
	expression?: string;
}

@Component({
	components: {
		"v-select": vSelect,
		"numeric": Numeric,
		"user-role-setup": UserRoleSetup,
		"definition-title": DefinitionTitle,
		"field-expression": FieldExpression,
		"add-to-date": AddToDate
	}
})
export default class CalculatedField extends Vue {
	@Prop()
	public item!: Field | null;

	@Prop()
	public value: any;

	@setup.State
	fields!: Field[];

	@Getter getIsFeatureEnabled!: (feature: TenantFeatures) => boolean

	@Getter("getCaseName") caseName!: () => string

	TenantFeatures = TenantFeatures

	get allFields (): Field[] {
		return [
			...caseColumns
				.filter(c => c.key !== "lastUpdated")
				.map(c => {
					return {
						id: c.key,
						title: c.label,
						dataType: c.dataType
					} as Field
				}),
			...this.fields
		]
	}

	private formatFieldsForDefinitionTitle = formatFieldsForDefinitionTitle

	// Enums used in mark up
	private calculationTypeEnum = CalculationType;

	private itemSourceDefinition: DataDefinitionWithFields | null = null;
	private dataDefinition: DataDefinitionWithFields | null = null;

	private filterFields: Field[] = [];

	private markerColors = MarkerColors;

	expressionType: string = "basic"

	private numericCalculationRadioMemory: NumericalCalculationParams = {}

	private calculationTypes: CalculationTypeOption[] = [
		{ key: CalculationType.Numerical, label: "Numerical" },
		{ key: CalculationType.Total, label: "Total" },
		{ key: CalculationType.Average, label: "Average" },
		{ key: CalculationType.AddToDate, label: "Add to date" },
		{ key: CalculationType.DaysBetweenDates, label: "Days between dates" },
		{ key: CalculationType.DaysUntilOrFromToday, label: "Days until or from today" },
		{ key: CalculationType.GreaterThan, label: "Greater Than" },
		{ key: CalculationType.LessThan, label: "Less Than" },
		{ key: CalculationType.TimeSpentAs, label: "Time Spent As" }
	];

	public get numericFields (): Field[] {
		const excludedCalculatedFieldTypes = [CalculationType.DaysUntilOrFromToday]

		let numericFields = this.fields.filter(fi => {
			const staticNumericFields = ["number", "dollar"]
			if (staticNumericFields.includes(fi.dataType!)) {
				return true
			}

			if (fi.dataType === "calculated") {
				const calculationType = (fi.extensions?.calculationType ?? null) as CalculationType | null
				if (calculationType === null) return false
				return (fi.id !== this.value.id && !excludedCalculatedFieldTypes.includes(calculationType) && CalculationDataTypeValue[calculationType] === DataType.number)
			}
		})
		// we also need to add on user-role fields that have time tracking enabled
		numericFields = numericFields.concat(this.fields.filter(fi => fi.dataType === "user-role" && fi.id !== this.value.id &&
		(fi.extensions &&
		(fi.extensions.allowTimeTracking ||
			(fi.extensions.subroles && fi.extensions.subroles.find((x: { allowTimeTracking: boolean; }) => x.allowTimeTracking === true))
		))))

		return numericFields
	}

	public get dateFields (): Field[] {
		return this.allFields.filter(fi => fi.dataType === "date" || fi.dataType === "date-time" ||
			(fi.dataType === "calculated" && fi.extensions &&
				(fi.extensions.calculationType === CalculationType.AddToDate)))
	}

	public get dataListFields (): Field[] {
		return this.fields.filter(f => f.definitionId)
	}

	public get dataSingleListFields (): Field[] {
		// only user defined fields currently supported (this.fields as opposed to this.allFields) as most built in fields do not recalculate calculated fields on change
		return this.fields.filter(f => f.dataType === "list")
	}

	public get dataItemFields (): Field[] {
		if (!this.dataDefinition) {
			return []
		}

		return this.dataDefinition.fields.filter(fi => fi.field.dataType === "number" || fi.field.dataType === "dollar").map(fi => fi.field)
	}

	public get dataSourceDefinitionFields (): Field[] {
		if (!this.itemSourceDefinition) {
			return []
		}

		return this.itemSourceDefinition.fields.map(fi => fi.field)
	}

	public get dataItemFieldsMatchType (): Field[] {
		if (!this.dataDefinition || !this.item) {
			return []
		}

		return this.dataDefinition.fields.filter(fi => fi.field.dataType === this.item!.dataType).map(fi => fi.field)
	}

	public get allowCalculatedOverride (): boolean {
		return this.item && this.item.dataType === "calculated" && this.value && this.value.calculationType === CalculationType.AddToDate
	}

	private get metricFieldWatchOptions (): LabeledValue[] {
		if (!this.item || this.item.dataType !== "field-metrics") {
			return []
		}

		const excludedDataTypes = ["field-metrics", "user-role", "calculated", "file-only"]
		const excludedFields = ["caseNumber", "isClosed"]

		return this.allFields.filter(f => f.dataType && !excludedDataTypes.includes(f.dataType))
			.filter(f => !excludedFields.includes(f.id!))
			.map(f => {
				return {
					label: f.title,
					value: f.id
				}
			})
	}

	private get addToDateValue () : CalculatedValueAddToDate {
		return {
			dateField: this.value.dateField,
			add: this.value.add,
			calculationType: this.value.calculationType,
			fields: this.value.fields,
			showTime: this.value.showTime,
			static: this.value.static
		}
	}

	mounted () {
		this.expressionType =
			this.value?.expression && this.getIsFeatureEnabled(TenantFeatures.CalculatedFieldExpression)
				? "expression"
				: "basic"

		if (this.expressionType === "basic" && this.value?.expression) {
			Vue.delete(this.value, "expression")
		}

		if (!this.value) {
			if (this.item && this.item.dataType !== "calculated") {
				this.$emit("input", {
					calculationType: null
				})
			} else {
				this.$emit("input", {
					calculationType: 0,
					fields: []
				})
			}
		} else {
			if (this.item && this.item.dataSourceId) {
				this.dataSourceChanged()
			}

			if (this.value.dataListField) {
				this.getDefinitionDetails(this.value.dataListField).then((definition: DataDefinitionWithFields | null) => {
					this.dataDefinition = definition
				})
			}

			if (this.value.calculationType !== undefined) {
				this.onCalculationTypeChanged(this.value.calculationType)
			}
		}
	}

	@Watch("item.dataSourceId")
	public async dataSourceChanged () {
		if (this.item && this.item.dataSourceId) {
			this.itemSourceDefinition = await api.definitionDetailsForSource(this.item.dataSourceId!)

			if (this.value.listFilter) {
				this.filterListValueSet(this.value.listFilter)
			}
		} else {
			this.itemSourceDefinition = null
		}
	}

	@Watch("value.calculationType")
	public onCalculationTypeChanged (calculationType: number | null) {
		if (calculationType === null) {
			if (!this.value.dateListField) {
				Vue.set(this.value, "dataListField", null)
			}
			if (!this.value.dataItemField) {
				Vue.set(this.value, "dataItemField", null)
			}
		} else if (calculationType === CalculationType.Numerical) {
			if (!this.value.add) {
				Vue.set(this.value, "add", [])
			}
			if (!this.value.subtract) {
				Vue.set(this.value, "subtract", [])
			}
		} else if (calculationType === CalculationType.AddToDate) {
			if (!this.value.dateField) {
				Vue.set(this.value, FieldExtensionsIds.DateField, null)
			}
			if (!this.value.add) {
				Vue.set(this.value, FieldExtensionsIds.Add, [])
			}
			if (!this.value.showTime) {
				Vue.set(this.value, FieldExtensionsIds.ShowTime, true)
			}
		} else if (calculationType === CalculationType.Total || calculationType === CalculationType.Average) {
			if (!this.value.dateListField) {
				Vue.set(this.value, "dataListField", null)
			}
			if (!this.value.dataItemField) {
				Vue.set(this.value, "dataItemField", null)
			}
		} else if (calculationType === CalculationType.DaysBetweenDates) {
			if (!this.value.startField) {
				Vue.set(this.value, "startField", null)
			}
			if (!this.value.endField) {
				Vue.set(this.value, "endField", null)
			}
		} else if (calculationType === CalculationType.DaysUntilOrFromToday) {
			if (!this.value.dateField) {
				Vue.set(this.value, "dateField", null)
			}
		} else if (calculationType === CalculationType.TimeSpentAs) {
			if (!this.value) {
				Vue.set(this.value, "trackingFieldId", "")
			}
		}

		this.$emit("input", this.value)
	}

	public updateValue () {
		this.$emit("input", this.value)
	}

	@Watch("value.dataListField")
	public async listFieldChanged (fieldId: string) {
		Vue.set(this.value, "dataItemField", null)

		if (!fieldId) {
			this.dataDefinition = null
			return
		}

		this.dataDefinition = await this.getDefinitionDetails(fieldId)
	}

	@Watch("item.dataType")
	public async dataTypeChanged () {
		if (this.value.dataItemField) {
			// If the data type has changed, the field it was reading from is probably no longer applicable
			if (!this.dataItemFieldsMatchType.find(f => f.id === this.value.dataItemField)) {
				this.value.dataItemField = null
			}
		}

		if (this.value.allowAddingToDataSource) {
			delete this.value.allowAddingToDataSource
		}
	}

	public async getDefinitionDetails (fieldId: string) {
		const dataListField = this.fields.find(f => f.id === fieldId)
		if (!dataListField || (!dataListField.dataSourceId && !dataListField.definitionId)) {
			return null
		}

		if (dataListField.definitionId) {
			return api.definitionDetails(dataListField.definitionId)
		} else {
			return api.definitionDetailsForSource(dataListField.dataSourceId!)
		}
	}

	@Watch("value.dateField")
	dateTimeFieldChanged (fieldId: string) {
		if (!fieldId) {
			Vue.set(this.value, "showTime", false)
			return
		}

		const dateField = this.allFields.find(f => f.id === fieldId)
		Vue.set(this.value, "showTime", dateField != null && dateField.dataType === "date-time")
	}

	public static stripUnusedOptions (field: Field, featureEnabled: (feature: TenantFeatures) => boolean) {
		if (!field.extensions) {
			return
		}

		if (field.dataType !== "calculated") {
			return
		}

		let allowedExtensions: any = ["calculationType", "fields"]

		switch (field.extensions.calculationType) {
			case CalculationType.Numerical:
				if (featureEnabled(TenantFeatures.MultiSelectKeepOpen)) {
					allowedExtensions = allowedExtensions.concat(["displayTime"])
				}
				if (field.extensions.expression) {
					// calculated field expression takes precedence over basic add/subtract
					allowedExtensions = allowedExtensions.concat(["static", "expression"])
					field.extensions.fields = getExpressionFieldIds(field.extensions.expression) as string[]
				} else {
					allowedExtensions = allowedExtensions.concat(["add", "subtract", "static"])
					field.extensions.fields = (field.extensions.add as string[]).concat(field.extensions.subtract as string[])
				}

				break
			case CalculationType.Total:
				if (featureEnabled(TenantFeatures.MultiSelectKeepOpen)) {
					allowedExtensions = allowedExtensions.concat(["displayTime"])
				}
				break
			case CalculationType.Average:
				if (featureEnabled(TenantFeatures.MultiSelectKeepOpen)) {
					allowedExtensions = allowedExtensions.concat(["displayTime"])
				}
				allowedExtensions = allowedExtensions.concat(["dataListField", "dataItemField", "static"])
				field.extensions.fields = [field.extensions.dataListField]

				break
			case CalculationType.AddToDate:
				allowedExtensions = allowedExtensions.concat(["add", "dateField", "static", "showTime", "allowOverride"])
				field.extensions.fields = [field.extensions.dateField, ...(field.extensions.add ? field.extensions.add as string[] : [])]

				break
			case CalculationType.DaysBetweenDates:
				allowedExtensions = allowedExtensions.concat(["startField", "endField"])
				field.extensions.fields = [field.extensions.startField, field.extensions.endField]

				break
			case CalculationType.DaysUntilOrFromToday:
				allowedExtensions = allowedExtensions.concat(["dateField"])
				field.extensions.fields = [field.extensions.dateField]

				break
			case CalculationType.GreaterThan:
			case CalculationType.LessThan:
				allowedExtensions = allowedExtensions.concat(["checkField", "otherField", "value", "allowEqual"])
				field.extensions.fields = [field.extensions.checkField, field.extensions.otherField]

				break
			case CalculationType.TimeSpentAs:
				allowedExtensions = allowedExtensions.concat(["trackingFieldId", "isClosed", "entries"])
				field.extensions.fields = [field.extensions.trackingFieldId, "isclosed"]

				break
		}

		const extensionFields = Object.keys(field.extensions)
		extensionFields.forEach(key => {
			if (allowedExtensions.indexOf(key) === -1) {
				delete field.extensions![key]
			}
		})
	}

	private filterListValueSet (definitionFieldId: string) {
		if (this.itemSourceDefinition) {
			const selectedValue = this.itemSourceDefinition.fields.find(f => f.field.id === definitionFieldId)

			if (selectedValue) {
				this.filterFields = this.fields.filter(f => f.dataType === selectedValue.field.dataType)

				return
			}
		}

		this.filterFields = []
	}
	private get closeOnSelect ():boolean {
		return !(this.getIsFeatureEnabled(TenantFeatures.MultiSelectKeepOpen))
	}

	private exportWithFormattedColumnChanged (checked: boolean) {
		if (checked) {
			// set default value of heading
			if (!this.value.exportColumnHeading) {
				Vue.set(this.value, "exportColumnHeading", (this.item?.title + " (Formatted)").trim())
			}
		}
	}

	/**
	 * Allows switching between 'basic' and 'expression' on numeric calculated fields without losing value
	 */
	@Watch("expressionType")
	private expressionTypeChanged (newValue: string) {
		// store current relevant values then try to restore existing values for other expression type
		if (newValue === "expression") {
			if (this.numericCalculationRadioMemory.expression) Vue.set(this.value, "expression", this.numericCalculationRadioMemory.expression)

			if (this.value.add) {
				this.numericCalculationRadioMemory.add = this.value.add
				Vue.delete(this.value, "add")
			}

			if (this.value.subtract) {
				this.numericCalculationRadioMemory.subtract = this.value.subtract
				Vue.delete(this.value, "subtract")
			}

			if (this.value.static) {
				this.numericCalculationRadioMemory.static = this.value.static
				Vue.delete(this.value, "static")
			}
		} else if (newValue === "basic") {
			if (this.numericCalculationRadioMemory.add) Vue.set(this.value, "add", this.numericCalculationRadioMemory.add)

			if (this.numericCalculationRadioMemory.subtract) Vue.set(this.value, "subtract", this.numericCalculationRadioMemory.subtract)

			if (this.numericCalculationRadioMemory.static) Vue.set(this.value, "static", this.numericCalculationRadioMemory.static)

			if (this.value.expression) {
				this.numericCalculationRadioMemory.expression = this.value.expression
				Vue.delete(this.value, "expression")
			}
		}
	}

	private addToDateValueChanged (valueArgs: AddToDateInputArgs): void {
		switch (valueArgs.propName) {
			case FieldExtensionsIds.DateField:
				this.value.dateField = valueArgs.value.dateField
				break
			case FieldExtensionsIds.Add:
				this.value.add = valueArgs.value.add
				break
			case FieldExtensionsIds.Static:
				this.value.static = valueArgs.value.static
				break
			default:
				break
		}
	}
}

